// PreviewTester/components/Tour/TourManager.js
import React, { useEffect } from 'react';
import { useTour } from './TourContext';
import TourModal from './TourModal';
import TourTooltip from './TourTooltip';
import {
  TOUR_STEPS,
  getWelcomeStep,
  getEditTemplateStep,
  getCustomMessageStep,
  getStartWizardStep,
  getSelectTextStep,
  getApplyTransformationStep,
  getSaveConfigurationStep,
  getCompleteStep,
} from './TourSteps';

const TourManager = ({
  isEditMode,
  showPhaseWizard,
  isSelectingMode,
  selectedText,
  showTransformationPanel,
  renderedPreview,
}) => {
  const {
    isActive,
    isCompleted,
    currentStep,
    nextStep,
    prevStep,
    completeTour,
    skipTour,
  } = useTour();

  // Auto-advance based on user actions
  useEffect(() => {
    if (!isActive || isCompleted) return;

    // Step 2: Edit Template → Step 3 (Custom Message)
    if (currentStep === TOUR_STEPS.EDIT_TEMPLATE && isEditMode) {
      setTimeout(() => nextStep(), 500);
    }

    // Step 3: Custom Message → Step 4 (Start Wizard)
    if (currentStep === TOUR_STEPS.CUSTOM_MESSAGE && !isEditMode) {
      setTimeout(() => nextStep(), 500);
    }

    // Step 4: Start Wizard → Step 5 (Select Text)
    if (currentStep === TOUR_STEPS.START_WIZARD && showPhaseWizard) {
      setTimeout(() => nextStep(), 500);
    }

    // Step 5: Select Text → Step 6 (Apply Transformation)
    if (currentStep === TOUR_STEPS.SELECT_TEXT && selectedText && selectedText.trim().length > 0) {
      setTimeout(() => nextStep(), 500);
    }

    // Step 6: Apply Transformation → Step 7 (Save Configuration)
    if (currentStep === TOUR_STEPS.APPLY_TRANSFORMATION && showTransformationPanel) {
      setTimeout(() => nextStep(), 500);
    }

  }, [isActive, isCompleted, currentStep, isEditMode, showPhaseWizard, isSelectingMode, selectedText, showTransformationPanel, nextStep]);

  // Don't render anything if tour is not active or completed
  if (!isActive || isCompleted || !renderedPreview) return null;

  // Get the current step data
  const getStepData = () => {
    switch (currentStep) {
      case TOUR_STEPS.WELCOME:
        return getWelcomeStep();
      case TOUR_STEPS.EDIT_TEMPLATE:
        return getEditTemplateStep();
      case TOUR_STEPS.CUSTOM_MESSAGE:
        return getCustomMessageStep();
      case TOUR_STEPS.START_WIZARD:
        return getStartWizardStep();
      case TOUR_STEPS.SELECT_TEXT:
        return getSelectTextStep();
      case TOUR_STEPS.APPLY_TRANSFORMATION:
        return getApplyTransformationStep();
      case TOUR_STEPS.SAVE_CONFIGURATION:
        return getSaveConfigurationStep();
      case TOUR_STEPS.COMPLETE:
        return getCompleteStep();
      default:
        return null;
    }
  };

  const stepData = getStepData();
  if (!stepData) return null;

  const totalSteps = 8;
  const isLastStep = currentStep === TOUR_STEPS.COMPLETE;

  // Render modal for welcome and complete steps
  if (stepData.type === 'modal') {
    const isWelcome = currentStep === TOUR_STEPS.WELCOME;
    const isComplete = currentStep === TOUR_STEPS.COMPLETE;

    return (
      <TourModal
        isOpen={true}
        onClose={isComplete ? completeTour : nextStep}
        onNext={isComplete ? completeTour : nextStep}
        onSkip={skipTour}
        title={stepData.title}
        description={stepData.description}
        icon={stepData.icon}
        buttonText={stepData.buttonText}
        isLast={isComplete}
      />
    );
  }

  // Render tooltip for other steps
  // Only show tooltip if the target element exists
  const targetExists = stepData.targetId && document.getElementById(stepData.targetId);
  if (!targetExists) return null;

  return (
    <TourTooltip
      isOpen={true}
      onNext={nextStep}
      onSkip={skipTour}
      title={stepData.title}
      description={stepData.description}
      icon={stepData.icon}
      position={stepData.position || 'bottom'}
      targetId={stepData.targetId}
      step={currentStep}
      totalSteps={totalSteps}
    />
  );
};

export default TourManager;